/**
 * testSession.js — how the console hands the booth an admin session.
 *
 * ⚠️ THE TEST IS THE BOOTH ITSELF. Not a copy of it, not a file picker, not a
 * second camera screen. Front camera, oval, countdown, "smile", blur check,
 * retakes, the lot — because a comparison run on frames a guest would never
 * produce tells you about the frames, not the painters (`D-45`).
 *
 * So the only thing test mode changes is what happens AFTER the shutter: the
 * same frames go to several painters instead of one, and the results come back
 * side by side.
 *
 * ⚠️ WHY sessionStorage AND NOT A URL. The payload contains a booth token
 * marked `admin` (`D-44`). A URL is copied, pasted into chat, screenshotted and
 * left in history; sessionStorage dies with the tab and never leaves the
 * device. It is also per-tab, so a booth open in another tab is untouched.
 *
 * ⚠️ AND NOT localStorage. `boothSession.js` says it out loud: nothing about a
 * guest session may outlive the visit. A test session that survived the tab
 * would eventually paint a stranger's portrait four times on somebody else's
 * money.
 */
const KEY = 'sp.test.session';

/**
 * Written by the console's Testing panel immediately before it opens the booth.
 *
 * `variants` is the frame-subset dimension (`D-46`): each entry is
 * `{ label, idx }`, where `idx` picks frames out of the ONE capture by
 * position. `[{label:'1+2+3', idx:[0,1,2]}]` is the plain painter comparison.
 * Painters x variants is the cross product, and it is run in waves.
 */
export function startTestSession({ token, exp, painters, variants, batch, tenant, event }) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({
      token, exp, painters, variants, batch, tenant, event,
    }));
    return true;
  } catch {
    /* Private mode, or storage disabled. The caller must not proceed as if a
       test session exists - the booth would run as an ordinary guest and paint
       one portrait, silently, at the client's expense. */
    return false;
  }
}

/**
 * Returns the live test session, or null.
 *
 * ⚠️ EXPIRY IS CHECKED HERE, ONCE. A booth token lives 90 minutes; a stale one
 * fails at the server with a 401 the guest cannot read, which would surface as
 * "the booth is broken" rather than "your test session ran out".
 */
export function loadTestSession(scope) {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s?.token || !Array.isArray(s.painters) || !s.painters.length) return null;
    if (!Array.isArray(s.variants) || !s.variants.length) return null;
    if (typeof s.exp === 'number' && Date.now() > s.exp - 30_000) { endTestSession(); return null; }
    if (scope && (s.tenant !== scope.tenant || s.event !== scope.event)) return null;
    return s;
  } catch { return null; }
}

export function endTestSession() {
  try { sessionStorage.removeItem(KEY); } catch { /* nothing to clean up */ }
}
